import { formatDate } from '@/lib/utils';
import { useAdminPermissions } from '@/contexts/AdminPermissionsContext';
import {
  X,
  CheckCircle,
  XCircle,
  Send,
  Mail,
  Phone,
  MapPin,
  Heart,
  Users,
  Clock,
  Quote,
} from 'lucide-react';

export interface BetaApp {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string | null;
  location: string | null;
  relationship_status: string | null;
  why_early_access: string | null;
  friends_to_invite: number | null;
  application_status: 'pending' | 'approved' | 'rejected' | 'invited';
  reviewed_at: string | null;
  links_sent_at: string | null;
  rejection_reason: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  channel: string | null;
  gclid: string | null;
  fbclid: string | null;
  ttclid: string | null;
  created_at: string;
  joined?: boolean;
}

export const STATUS_STYLES: Record<BetaApp['application_status'], string> = {
  pending: 'bg-amber-100 text-amber-700',
  approved: 'bg-blue-100 text-blue-700',
  rejected: 'bg-gray-100 text-gray-600',
  invited: 'bg-green-100 text-green-700',
};

export function StatusBadge({ status }: { status: BetaApp['application_status'] }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${STATUS_STYLES[status]}`}
    >
      {status}
    </span>
  );
}

interface Props {
  app: BetaApp;
  onClose: () => void;
  /** Action handlers; the parent owns the API calls + refetch. */
  onApprove: (app: BetaApp) => void;
  onReject: (app: BetaApp) => void;
  onSendLinks: (app: BetaApp) => void;
  busy?: boolean;
}

function Field({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof Mail;
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
      <div className="min-w-0">
        <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
        <p className="break-words text-sm font-medium">{value || '—'}</p>
      </div>
    </div>
  );
}

function AttrRow({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="flex items-center justify-between gap-4 py-1 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="truncate font-mono text-xs" title={value || undefined}>
        {value || '—'}
      </span>
    </div>
  );
}

export function BetaDetailDrawer({
  app,
  onClose,
  onApprove,
  onReject,
  onSendLinks,
  busy,
}: Props) {
  const { hasPermission } = useAdminPermissions();
  const canReview = hasPermission('growth.beta.review');
  const canInvite = hasPermission('growth.beta.invite');

  const fullName = `${app.first_name ?? ''} ${app.last_name ?? ''}`.trim() || '—';

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/50" onClick={onClose}>
      <div
        className="flex h-full w-full max-w-lg flex-col bg-card shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between border-b p-6">
          <div className="min-w-0">
            <div className="flex items-center gap-3">
              <h3 className="truncate text-lg font-semibold">{fullName}</h3>
              <StatusBadge status={app.application_status} />
            </div>
            <p className="mt-1 truncate text-sm text-muted-foreground">{app.email}</p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="rounded-lg p-2 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 space-y-6 overflow-y-auto p-6">
          {/* Why early access */}
          <div className="rounded-lg border bg-muted/30 p-4">
            <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <Quote className="h-3.5 w-3.5" />
              Why early access
            </div>
            <p className="whitespace-pre-wrap text-sm leading-relaxed">
              {app.why_early_access || '—'}
            </p>
          </div>

          {/* Applicant details */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field icon={Mail} label="Email" value={app.email} />
            <Field icon={Phone} label="Phone" value={app.phone} />
            <Field icon={MapPin} label="City" value={app.location} />
            <Field icon={Heart} label="Relationship status" value={app.relationship_status} />
            <Field
              icon={Users}
              label="Friends to invite"
              value={app.friends_to_invite ?? 0}
            />
          </div>

          {/* Attribution */}
          <div>
            <h4 className="mb-2 text-sm font-semibold">Attribution</h4>
            <div className="rounded-lg border p-4">
              <AttrRow label="Channel" value={app.channel} />
              <AttrRow label="UTM source" value={app.utm_source} />
              <AttrRow label="UTM medium" value={app.utm_medium} />
              <AttrRow label="UTM campaign" value={app.utm_campaign} />
              <AttrRow label="gclid" value={app.gclid} />
              <AttrRow label="fbclid" value={app.fbclid} />
              <AttrRow label="ttclid" value={app.ttclid} />
            </div>
          </div>

          {/* Timeline */}
          <div>
            <h4 className="mb-2 text-sm font-semibold">Timeline</h4>
            <div className="space-y-3 rounded-lg border p-4">
              <div className="flex items-center gap-3 text-sm">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Applied</span>
                <span className="ml-auto font-medium">{formatDate(app.created_at)}</span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <CheckCircle className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Reviewed</span>
                <span className="ml-auto font-medium">
                  {app.reviewed_at ? formatDate(app.reviewed_at) : '—'}
                </span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <Send className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Links sent</span>
                <span className="ml-auto font-medium">
                  {app.links_sent_at ? formatDate(app.links_sent_at) : '—'}
                </span>
              </div>
              {app.application_status === 'rejected' && app.rejection_reason && (
                <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                  <span className="font-medium">Rejection reason: </span>
                  {app.rejection_reason}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Actions */}
        {(canReview || canInvite) && (
          <div className="flex flex-wrap items-center justify-end gap-2 border-t p-4">
            {canReview && app.application_status === 'pending' && (
              <>
                <button
                  onClick={() => onReject(app)}
                  disabled={busy}
                  className="flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium hover:bg-muted disabled:opacity-50"
                >
                  <XCircle className="h-4 w-4" />
                  Reject
                </button>
                <button
                  onClick={() => onApprove(app)}
                  disabled={busy}
                  className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                >
                  <CheckCircle className="h-4 w-4" />
                  Approve
                </button>
              </>
            )}
            {canInvite &&
              (app.application_status === 'approved' || app.application_status === 'invited') && (
                <button
                  onClick={() => onSendLinks(app)}
                  disabled={busy}
                  className="flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
                >
                  <Send className="h-4 w-4" />
                  {app.application_status === 'invited' ? 'Resend Links' : 'Send Links'}
                </button>
              )}
          </div>
        )}
      </div>
    </div>
  );
}
